import { toast } from 'vue-sonner'
import { getCookie, setCookie } from '~/composables/cookies'

export const refreshPromise = ref<Promise<unknown> | null>(null)

type RequestType = 'auth'|'search'|'lookup'|'api'

// Function to refresh the access token using the refresh token
async function refreshToken () {
  const promise = (async () => {
    const refreshToken = getCookie('refresh_token')
    if (!refreshToken) {
      return false
    }

    try {
      const { public: { authHost, authClientId } } = useRuntimeConfig()

      const data = new FormData()

      data.append('grant_type', 'refresh_token')
      data.append('refresh_token', refreshToken)
      data.append('client_id', authClientId)

      const res = await $fetch<{access_token: string, refresh_token: string}>(`${authHost}/oauth/token`, {
        method: 'POST',
        body: data
      })

      setCookie('access_token', res.access_token, 15)
      setCookie('refresh_token', res.refresh_token, 15)

      // Also refresh the lexsearch token
      if (await fetchLexSearchToken() === false) {
        return false
      }

      return true
    } catch (error) {
      return false
    } finally {
      refreshPromise.value = null
    }
  })()
  refreshPromise.value = promise
  return await promise
}

export async function fetchLexSearchToken() {
  try {
    const { public: { authHost } } = useRuntimeConfig()
    const lexsearchTokenResponses = await $fetch<any>(`${authHost}/api/lexsearch/tokens/create`, {
      headers: { Authorization: `Bearer ${getCookie('access_token')}`, Accept: 'application/json' }
    })
    setCookie('lexsearch_token', lexsearchTokenResponses.token, 15)
    return true
  } catch (error: any) {
    if (error?.response?.status === 402) {
      setCookie('lexsearch_token', 'invalid')
      return true
    }
    toast.error('Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.')
    return false
  }
}

async function retryFetchWithNewToken (type: RequestType, url: string, options: any = {}): Promise<any> {
  // Ensure options.headers exists
  if (!options.headers) {
    options.headers = {}
  }

  // Update the Authorization header with the new token
  if ((['search', 'lookup'] as RequestType[]).includes(type)) {
    const lexsearchToken = getCookie('lexsearch_token')
    if (lexsearchToken === 'invalid') {
      toast.error('Sie benötigen ein JusBase Abonnement um diese Funktion zu verwenden.')
      return undefined
    }
    options.headers.Authorization = `Bearer ${lexsearchToken}`
  } else {
    const accessToken = getCookie('access_token')
    options.headers.Authorization = `Bearer ${accessToken}`
  }

  // Retry the original request with the new token
  return await fetchWithErrorHandling(type, url, options, true)
}

// Helper function to handle token refresh and retry
async function handleTokenRefresh (type: RequestType, url: string, options: any = {}): Promise<any> {
  const { clearSession } = useUserInfo()

  const refreshSuccessful = await refreshToken()

  if (refreshSuccessful) {
    return await retryFetchWithNewToken(type, url, options)
  } else {
    // If refresh failed, clear session and show alert
    clearSession()
    toast.error('Ihre Sitzung ist abgelaufen.', {
      duration: 100000,
      action: {
        label: 'Erneut anmelden',
        onClick: () => {
          setCookie('intendedUrl', useRoute().fullPath)
          navigateTo('/login')
        }
      }
    })
  }
}

export async function fetchFromAuthHost<T> (endpoint: string, options?: Parameters<typeof $fetch<T>>[1]) {
  const { public: { authHost } } = useRuntimeConfig()
  return await fetchWithErrorHandling<T>('auth', `${authHost}${endpoint}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getCookie('access_token')}`,
      Accept: 'application/json'
    },
    ...options
  })
}

export async function fetchFromLookupHost<T> (endpoint: string, options?: Parameters<typeof $fetch<T>>[1]) {
  const { public: { lookupHost } } = useRuntimeConfig()
  return await fetchWithErrorHandling<T>('lookup', `${lookupHost}${endpoint}`, {
    headers: {
      Authorization: `Bearer ${getCookie('lexsearch_token')}`,
      Accept: 'application/json'
    },
    ...options
  })
}

export async function fetchFromSearchHost<T> (endpoint: string, options?: Parameters<typeof $fetch<T>>[1]) {
  const { public: { searchHost } } = useRuntimeConfig()
  const token = getCookie('lexsearch_token')
  if (token === 'invalid') {
    toast.error('Sie benötigen ein JusBase Abonnement um diese Funktion zu verwenden.')
    return undefined
  }
  return await fetchWithErrorHandling<T>('search', `${searchHost}${endpoint}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json'
    },
    ...options
  })
}

export async function fetchFromAPI<T> (endpoint: string, options?: Parameters<typeof $fetch<T>>[1]) {
  return await fetchWithErrorHandling<T>('api', `${endpoint}`, {
    headers: {
      Authorization: `Bearer ${getCookie('lexsearch_token')}`,
      Accept: 'application/json'
    },
    ...options
  })
}

async function fetchWithErrorHandling<T> (type: RequestType, requestUrl: string, options: Parameters<typeof $fetch<T>>[1] = {}, skipRefreshPromise = false) : Promise<T> {
  try {
    if (refreshPromise.value && !skipRefreshPromise) {
      await refreshPromise.value
    }

    // For redirects, we need to handle them manually to detect auth-related redirects
    if (!options.redirect) {
      options.redirect = 'manual'
    }

    const response = await $fetch.raw<any>(requestUrl, options)

    // Check if this is a redirect (indicating auth failure)
    // We don't need to check the specific URL since we're handling all redirects as auth issues
    if (response.type === 'opaqueredirect' || response.status === 302 || response.status === 301) {
      if (refreshPromise.value) {
        await refreshPromise.value
        return await retryFetchWithNewToken(type, requestUrl, options)
      } else {
        return await handleTokenRefresh(type, requestUrl, options)
      }
    }

    // If not a redirect, return the response data
    return response._data
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.error('Fetch error', error.response)

    let errorMessage: string

    switch (error?.response?.status) {
      case 401:
        if (refreshPromise.value) {
          await refreshPromise.value
          return await retryFetchWithNewToken(type, requestUrl, options)
        } else {
          return await handleTokenRefresh(type, requestUrl, options)
        }
      case 402:
        errorMessage = 'Sie benötigen ein JusBase Abonnement um diese Funktion zu verwenden.'
        break
      case 403:
        errorMessage = 'Auf diese Seite kann nicht zugegriffen werden.'
        break
      case 404:
        errorMessage = 'Die angefragte Seite konnte nicht gefunden werden.'
        break
      case 406:
        errorMessage = 'Die Anfrage konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.'
        break
      case 500:
        errorMessage = 'Es ist ein unerwarteter Fehler aufgetreten. Bitte melden Sie sich beim Support.'
        break
      case 503:
        errorMessage = 'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es in ein paar Minuten erneut.'
        break
      default:
        errorMessage = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.'
        break
    }

    toast.error(errorMessage)

    throw error
  }
}
